// Left-rail navigation. Filters nav items by the current user's
// role tier (matches backend require_role() assignments in
// app/main.py).
//
// Substep 5-η.6: identity + Sign Out moved to TopBar; sidebar now
// carries brand + logo + grouped nav sections + minimal footer. Nav
// items grouped under section headers (MATTERS / DOCUMENTS / FIRM /
// PLATFORM) so the 18-item flat list stops feeling like a wall.
//
// Two visual modes — `collapsed=false` (240px, full labels + section
// headers) or `collapsed=true` (64px, icons only with title tooltips
// and no section headers).

import React, { useState } from "react";
import { C, roleAtLeast } from "../constants.js";
import { I } from "../icons.jsx";

// Nav sections.
//
// Rebuilt 2026-09-10. It had grown to 28 items across 6 sections, and
// two things were wrong with it beyond length.
//
// FIRST, the collapsed rail did not work. At 64px it is icons and
// nothing else, and 28 items shared 13 icons — `I.doc` alone covered
// Templates, Case Costs Out, Reports, Firm Settings, Firm SOPs, Firm
// Directory and Platform Firms. Seven identical squares. Every item
// below now has an icon nobody else uses.
//
// SECOND, setup sat beside daily work. Six configuration screens and
// two platform screens held permanent rows in a rail a paralegal reads
// twenty times a day, for things touched monthly at best. They live
// behind Settings and Platform now — one click further, and out of the
// way of the glance that matters.
//
// Two merges of things that were one subject split in two: Deadlines
// absorbed Statute Tracking (what is tracked, and what is not), and
// Finance absorbed the separate Revenue row (FinancePage already had
// the tab; it was two nav items for one page).
//
// Result: 28 items to 17 for a platform operator, 26 to 16 for a firm
// admin, 14 to 12 for a paralegal.
//
// Section headers only render when at least one item passes the role
// filter.
const NAV_SECTIONS = [
  {
    // Ungrouped top item — the landing page, and the only thing a
    // pending user sees.
    key: null,
    items: [
      { id: "home",                 icon: I.home,     label: "My Day",          minRole: "pending" },
    ],
  },
  {
    key: "Matters",
    items: [
      { id: "matters",              icon: I.folder,   label: "Matters",         minRole: "paralegal" },
      { id: "new-matter",           icon: I.plus,     label: "New Matter",      minRole: "attorney" },
      { id: "pipeline",             icon: I.layers,   label: "Pipeline",        minRole: "paralegal" },
      // Cross-matter, and run BEFORE opening one.
      { id: "conflicts",            icon: I.search,   label: "Conflict Check",  minRole: "paralegal" },
      // Absorbs the old "Statute Tracking" row as a second tab. Both
      // are dates the firm owes; the second is the ones nobody is
      // watching yet, which is only meaningful beside the first.
      { id: "deadlines",            icon: I.clock,    label: "Deadlines",       minRole: "paralegal" },
      { id: "firm-feed",            icon: I.activity, label: "Firm Feed",       minRole: "paralegal" },
    ],
  },
  {
    key: "Documents",
    items: [
      { id: "generate",             icon: I.zap,      label: "Generate Doc",    minRole: "paralegal" },
      { id: "templates",            icon: I.doc,      label: "Templates",       minRole: "paralegal" },
      { id: "review-queue",         icon: I.inbox,    label: "Review Queue",    minRole: "paralegal" },
      { id: "upload-folder",        icon: I.upload,   label: "Upload Folder",   minRole: "paralegal" },
      { id: "gen-history",          icon: I.refresh,  label: "Gen History",     minRole: "paralegal" },
    ],
  },
  {
    key: "Firm",
    items: [
      { id: "firm-overview",        icon: I.chart,    label: "Firm Overview",   minRole: "partner" },
      { id: "reports",              icon: I.trend,    label: "Reports",         minRole: "partner" },
      // One item, two tabs. FinancePage already owned the costs/revenue
      // toggle — the second nav row was pointing at the same page with
      // a different default.
      { id: "finance",              icon: I.money,    label: "Finance",         minRole: "attorney" },
      { id: "firm-sops",            icon: I.book,     label: "Firm SOPs",       minRole: "paralegal" },
      { id: "firm-directory",       icon: I.contacts, label: "Firm Directory",  minRole: "paralegal" },
    ],
  },
  {
    key: null,
    items: [
      // Six configuration screens live inside this one.
      { id: "settings",             icon: I.settings, label: "Settings",        minRole: "partner" },
      { id: "platform",             icon: I.shield,   label: "Platform",        minRole: "platform_operator" },
    ],
  },
];

export function Sidebar({ page, setPage, authStatus, currentFirm, collapsed = false,
                         onToggle, isMobile = false, open = false, onClose }) {
  const role = authStatus?.firm_user?.role || "pending";

  // Firm brand strings — fall back to sensible generic values if the
  // /api/current-firm fetch hasn't landed yet (first paint) or failed.
  const shortName = currentFirm?.short_name || "";
  const displayName = currentFirm?.display_name || "";
  let brandLine1 = displayName, brandLine2 = "";
  const ampIdx = displayName.indexOf(" & ");
  if (ampIdx > 0) {
    brandLine1 = displayName.slice(0, ampIdx);
    brandLine2 = displayName.slice(ampIdx + 1);
  }
  let collapsedMonogram = shortName.slice(0, 3);
  if (shortName.length >= 2) {
    collapsedMonogram = null;
  }

  // Substep 5-η.6.f: firm logo removed from sidebar per Aaron —
  // didn't sit well against the navy without extra styling. Text
  // brand only. Logo still lives at GET /api/current-firm/logo for
  // document generation ({{firm_logo}}) and future callers.

  // Substep 5-η.6.e: collapsible nav sections. Set of section keys the
  // user has collapsed. Persisted in localStorage so preferences stick
  // across reloads. Default: all expanded.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem("charpen.sidebar.collapsedSections");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try {
        localStorage.setItem("charpen.sidebar.collapsedSections",
          JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  // Filter items per section by role, then drop empty sections.
  const visibleSections = NAV_SECTIONS
    .map(sec => ({ ...sec, items: sec.items.filter(i => roleAtLeast(role, i.minRole)) }))
    .filter(sec => sec.items.length > 0);

  const width = collapsed ? 64 : 240;

  // Chevron toggle — same as before, moves between top-right corner
  // (expanded) and below-monogram (collapsed).
  // On mobile this closes the drawer instead of collapsing it —
  // collapsing a drawer to 64px would leave a sliver hanging over the
  // content, which is not a state anyone wants. Bigger there too: 22px
  // is a fine mouse target and a poor thumb one.
  const toggleButton = React.createElement("button", {
    onClick: isMobile ? onClose : onToggle,
    "aria-label": isMobile ? "Close navigation"
                           : (collapsed ? "Expand sidebar" : "Collapse sidebar"),
    title: isMobile ? "Close" : (collapsed ? "Expand sidebar" : "Collapse sidebar"),
    style: {
      ...(collapsed && !isMobile
        ? { display: "block", margin: "10px auto 0" }
        : { position: "absolute",
            // Lower and further in on a phone. The top-right corner of
            // a full-height drawer is exactly where the Dynamic Island
            // sits, and a control there is not merely awkward — it is
            // untappable.
            top: isMobile ? 18 : 12,
            right: isMobile ? 16 : 12 }
      ),
      ...(isMobile
        ? { width: 38, height: 38, borderRadius: 10 }
        : { width: 22, height: 22, borderRadius: 6 }),
      background: "rgba(255,255,255,0.06)",
      border: `1px solid rgba(255,255,255,0.12)`,
      color: "rgba(255,255,255,0.7)",
      cursor: "pointer",
      padding: 0,
      transition: "all 0.15s",
      display: "flex", alignItems: "center", justifyContent: "center",
      ...(collapsed ? { marginLeft: "auto", marginRight: "auto" } : {}),
    },
    onMouseEnter: e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; },
    onMouseLeave: e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; },
  },
    React.createElement("svg", {
      width: isMobile ? 17 : 12, height: isMobile ? 17 : 12, viewBox: "0 0 24 24",
      fill: "none", stroke: "currentColor", strokeWidth: 2.5, strokeLinecap: "round",
      style: isMobile ? {} : {
        transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
        transition: "transform 0.18s ease",
      },
    },
      // A cross, not a chevron. The chevron means "collapse to a
      // narrower rail", which is not what the button does on a phone.
      isMobile
        ? React.createElement(React.Fragment, null,
            React.createElement("line", { x1: 18, y1: 6, x2: 6, y2: 18 }),
            React.createElement("line", { x1: 6, y1: 6, x2: 18, y2: 18 }),
          )
        : React.createElement("polyline", { points: "9 18 15 12 9 6" }),
    ),
  );

  // Brand block — expanded mode: logo (if uploaded) + firm name + "Document Agent";
  // collapsed mode: monogram (skip logo — too small at 64px to be legible).
  const brandBlock = collapsed
    ? React.createElement("div", {
        style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:700,
                 color:"#fff", lineHeight:1, textAlign: "center" },
      },
        collapsedMonogram !== null
          ? collapsedMonogram
          : [
              shortName.charAt(0),
              React.createElement("span", { key: "amp", style: { color: C.gold } }, "&"),
              shortName.charAt(shortName.length - 1),
            ]
      )
    : React.createElement(React.Fragment, null,
        // Text brand only (Substep 5-η.6.f — logo removed).
        React.createElement("div", {
          style: {
            fontFamily:"'Playfair Display',serif",
            fontSize: "18px", fontWeight: 700,
            color: "#fff", lineHeight: 1.2,
          }
        },
          brandLine1.replace(/,?\s*&$/, ""),
        ),
        brandLine2 && React.createElement("div", {
          style: {
            fontFamily:"'Playfair Display',serif",
            fontSize: "18px", fontWeight: 700,
            color: "#fff",
          }
        },
          React.createElement("span", { style: { color: C.gold } }, "&"),
          brandLine2.replace(/^&/, ""),
        ),
        React.createElement("div", {
          style: {
            fontSize: "10px", color: C.goldLight,
            marginTop: "6px", letterSpacing: "0.08em",
            textTransform: "uppercase", fontWeight: 500,
          }
        }, "Document Agent"),
      );

  // On a phone the sidebar is a drawer, not a column. At 240px wide it
  // would otherwise occupy 64% of a 375px screen permanently, which is
  // what made the installed app feel crammed: the content had 135px to
  // live in.
  const shellStyle = isMobile
    ? {
        position: "fixed", left: 0, top: 0, bottom: 0,
        // Near full width on a phone. At 270px of a 375px screen the
        // remaining 105px showed a sliver of the page behind — half a
        // card, a clipped heading — which read as broken rather than as
        // layered. Leaving ~45px keeps tap-outside-to-close working
        // while the page effectively disappears.
        width: "min(86vw, 320px)",
        background: C.navy, display: "flex", flexDirection: "column",
        borderRight: `1px solid ${C.navyLight}`,
        // Slid off-screen rather than unmounted, so opening it animates
        // and the scroll position inside survives.
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.22s ease",
        zIndex: 120,
        overflowY: "auto",
        // Clears the notch and the home indicator on an installed PWA,
        // where there is no browser chrome to do it for us.
        paddingTop: "max(env(safe-area-inset-top), 10px)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }
    : {
        position: "fixed", left: 0, top: 0, bottom: 0, width: `${width}px`,
        background: C.navy, display: "flex", flexDirection: "column",
        borderRight: `1px solid ${C.navyLight}`,
        transition: "width 0.18s ease",
        overflow: "hidden",
      };

  return React.createElement(React.Fragment, null,
    // Scrim. Tapping outside is how people expect to dismiss a drawer,
    // and without it the only way out is the close button.
    isMobile && open && React.createElement("div", {
      onClick: onClose,
      style: {
        position: "fixed", inset: 0, background: "rgba(15,20,35,0.62)",
        zIndex: 110,
      },
    }),
    React.createElement("div", { style: shellStyle },
    // Brand section
    React.createElement("div", {
      style: {
        position: "relative",
        padding: collapsed ? "20px 0 18px" : "20px 20px 18px",
        borderBottom: `1px solid ${C.navyLight}30`,
      },
    },
      brandBlock,
      toggleButton,
    ),

    // Nav sections
    React.createElement("nav", {
      style: {
        padding: collapsed ? "12px 6px" : "12px 12px",
        flex: 1, overflowY: "auto",
      }
    },
      ...visibleSections.map((sec, secIdx) => {
        const isSectionCollapsed = !!(sec.key && collapsedSections.has(sec.key));
        // Ungrouped block (no key) never collapses. Items always render.
        const showItems = collapsed || !sec.key || !isSectionCollapsed;

        return React.createElement("div", {
          key: sec.key || `_top_${secIdx}`,
          style: { marginBottom: "10px" },
        },
          // Section header — clickable to collapse/expand (expanded
          // sidebar mode only, and only when the section has a name).
          !collapsed && sec.key && React.createElement("button", {
            onClick: () => toggleSection(sec.key),
            "aria-label": `${isSectionCollapsed ? "Expand" : "Collapse"} ${sec.key} section`,
            style: {
              width: "100%",
              display: "flex", alignItems: "center", gap: "6px",
              fontSize: "10px", color: "rgba(255,255,255,0.35)",
              fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "6px 12px 4px",
              marginTop: secIdx === 0 ? 0 : "6px",
              background: "transparent", border: "none",
              cursor: "pointer", textAlign: "left",
              transition: "color 0.12s",
            },
            onMouseEnter: e => { e.currentTarget.style.color = "rgba(255,255,255,0.6)"; },
            onMouseLeave: e => { e.currentTarget.style.color = "rgba(255,255,255,0.35)"; },
          },
            React.createElement("span", {
              style: {
                display: "inline-block", width: "8px",
                fontSize: "9px", opacity: 0.7,
                transform: isSectionCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.15s",
              }
            }, "▾"),
            sec.key,
          ),

          // Items
          showItems && sec.items.map(item => {
            const isActive = page === item.id || (item.id === "matters" && page === "matter");
            return React.createElement("button", {
              key: item.id,
              onClick: () => setPage(item.id),
              title: collapsed ? item.label : undefined,
              "aria-label": item.label,
              style: {
                width:"100%",
                display:"flex", alignItems:"center",
                justifyContent: collapsed ? "center" : "flex-start",
                gap:"10px",
                padding: collapsed ? "10px 0" : "9px 12px",
                borderRadius:"8px", border:"none",
                background: isActive ? `${C.gold}18` : "transparent",
                color: isActive ? C.goldLight : "rgba(255,255,255,0.6)",
                cursor:"pointer", fontSize:"14px", fontWeight:500,
                marginBottom:"2px", textAlign:"left", transition:"all 0.15s",
                borderLeft: collapsed
                  ? "3px solid transparent"
                  : (isActive ? `3px solid ${C.gold}` : "3px solid transparent"),
                borderBottom: collapsed && isActive ? `2px solid ${C.gold}` : undefined,
              },
              onMouseEnter: e => { if (!isActive) { e.currentTarget.style.background = C.navyLight; e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }},
              onMouseLeave: e => { if (!isActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }},
            },
              item.icon,
              !collapsed && item.label,
            );
          }),
        );
      }),
    ),

    // Substep 5-η.6.e: footer text removed per Aaron.
  ),
  );
}
